import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Target, Plus, Minus, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { WEB_ACCESS, getWebAccessLevel, isWebAdmin } from '@/auth/webAccess';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import GeoFenceModal from '@/components/GeoFenceModal';
import AcknowledgmentModal from '@/components/AcknowledgmentModal';
import ResponseActionModal from '@/components/ResponseActionModal';
import StatusUpdateModal from '@/components/StatusUpdateModal';
import {
  announceIncidentTimelineChange,
  buildGeoFenceTargeting,
  isGeoFenceOperationalIncident,
  manualNotificationOutcome,
} from '@/components/geoFenceActions';
import { buildZoneIndex, resolveIncidentCoords } from '@/components/map/zoneCoords';
import {
  GOOGLE_MAPS_LOADER_OPTIONS, usePersistentGisMap, GisSaveControl, MapStyleSelector, buildMapStyleOptions,
  MapGisPanel, MapGisKpi, MapGisOverlay, TaskFormModal, PointFormModal, FULL_MAP_IMPORTANCE_COLORS,
} from '@/components/map/MapGisTools';
import { useMapsUnavailable, MapUnavailable } from '@/components/map/MapUnavailable';
import { useIncidentPersonnel, PersonnelMarkers } from '@/components/map/IncidentPersonnel';
import { keepNewestCreatedTimelineFocus } from '@/components/timelineFocusController';

const containerStyle = { width: '100%', height: '100%' };
const fallbackCenter = { lat: 24.7136, lng: 46.6753 }; // used only until Operations Area loads

const spreadOverlappingFamilyMarkers = (markers, selectedId) => {
  const groups = new Map();
  markers.forEach((marker) => {
    const key = `${Number(marker.lat).toFixed(5)}:${Number(marker.lng).toFixed(5)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(marker);
  });
  return markers.map((marker) => {
    const key = `${Number(marker.lat).toFixed(5)}:${Number(marker.lng).toFixed(5)}`;
    const group = groups.get(key) || [];
    if (group.length < 2) return marker;
    const ordered = [...group].sort((a, b) => (a.id === selectedId ? -1 : b.id === selectedId ? 1 : String(a.id).localeCompare(String(b.id))));
    const index = ordered.findIndex((entry) => entry.id === marker.id);
    if (index <= 0) return marker;
    const ring = Math.ceil(index / 8);
    const angle = ((index - 1) % 8) * (Math.PI / 4);
    const radius = 0.000055 * ring;
    return {
      ...marker,
      lat: Number(marker.lat) + Math.cos(angle) * radius,
      lng: Number(marker.lng) + Math.sin(angle) * radius,
      displayOffset: true,
    };
  });
};

// Dedicated, self-contained GIS map editor for a single Scenario or Drill.
// Intentionally NOT the sidebar "Map Module" (/map) — that page is the global
// system-wide operations interface. This page is scoped to one scenario/drill.
const ExerciseMapPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || null;

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();

  const [incident, setIncident] = useState(null);
  const [familyEntries, setFamilyEntries] = useState([]);
  const [zones, setZonesData] = useState([]); // location zones (for coord resolution)
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState(null);
  const mapFrameRef = useRef(null);
  const [selectedIncidentInfoId, setSelectedIncidentInfoId] = useState(null);
  const [selectedRelationId, setSelectedRelationId] = useState(null);
  const [actionCards, setActionCards] = useState([]);
  const [operationsCenter, setOperationsCenter] = useState(fallbackCenter);
  const [appUsers, setAppUsers] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [serverRecipientScope, setServerRecipientScope] = useState(null);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const isAdmin = useMemo(() => isWebAdmin(currentUser), [currentUser]);
  const isExplicitLeader = useMemo(
    () => getWebAccessLevel(currentUser) === WEB_ACCESS.LEADER,
    [currentUser]
  );
  const scopedLeaderGrants = useMemo(() => {
    if (!isExplicitLeader || !currentUser?.id) return [];
    return (incident?.scoped_leader_grants || []).filter(
      (grant) => String(grant?.leader_id || '') === String(currentUser.id)
    );
  }, [currentUser?.id, incident?.scoped_leader_grants, isExplicitLeader]);
  const isIncidentScopedLeader = scopedLeaderGrants.length > 0;
  const canEditGisMap = isAdmin;
  const incidentIsClosed = [
    'resolved', 'closed', 'completed', 'cancelled', 'canceled', 'false_alarm', 'false alarm',
  ].includes(String(incident?.status || '').trim().toLowerCase());
  const isReadOnly = incidentIsClosed || !canEditGisMap;

  const scopedRecipientScope = useMemo(() => {
    if (isAdmin) return null;
    if (serverRecipientScope) {
      return {
        userIds: [...new Set([...(serverRecipientScope.userIds || []), currentUser?.id].filter(Boolean).map(String))],
        departmentIds: [...new Set((serverRecipientScope.departmentIds || []).map(String))],
        teamIds: [...new Set((serverRecipientScope.teamIds || []).map(String))],
        crewIds: [...new Set((serverRecipientScope.crewIds || []).map(String))],
      };
    }

    const userIds = new Set();
    const departmentIds = new Set();
    const teamIds = new Set();
    const crewIds = new Set();
    if (!isIncidentScopedLeader) {
      return { userIds: [], departmentIds: [], teamIds: [], crewIds: [] };
    }
    if (currentUser?.id) userIds.add(String(currentUser.id));

    const resolveUserRef = (ref) => {
      const value = String(ref || '').toLowerCase();
      return (appUsers || []).find((user) => (
        [user.id, user.email, user.user_account, user.name]
          .some((key) => String(key || '').toLowerCase() === value)
      ));
    };

    scopedLeaderGrants.forEach((grant) => {
      const scopeId = String(grant?.scope_id || '');
      const scopeType = String(grant?.scope_type || '').toLowerCase();
      if (!scopeId) return;

      if (scopeType === 'department') {
        departmentIds.add(scopeId);
        const department = (allDepartments || []).find((row) => (
          String(row.id || row._id || '') === scopeId
          || String(row.department_name || row.name || '') === scopeId
        ));
        if (department?.id || department?._id) departmentIds.add(String(department.id || department._id));
        if (department?.department_name || department?.name) {
          departmentIds.add(String(department.department_name || department.name));
        }

        (department?.members || []).forEach((member) => {
          const memberRef = typeof member === 'object'
            ? (member.id || member.user_id || member.user_account)
            : member;
          const resolved = resolveUserRef(memberRef);
          const memberId = resolved?.id || memberRef;
          if (memberId) userIds.add(String(memberId));
        });

        const departmentRefs = new Set([
          scopeId,
          department?.id,
          department?._id,
          department?.department_name,
          department?.name,
        ].filter(Boolean).map(String));
        (appUsers || []).forEach((user) => {
          const userDepartments = [
            ...(Array.isArray(user.departments_units) ? user.departments_units : []),
            user.department_unit,
            user.department,
          ].filter(Boolean).map(String);
          if (userDepartments.some((ref) => departmentRefs.has(ref)) && user?.id) {
            userIds.add(String(user.id));
          }
        });
        return;
      }

      const matchingTeams = scopeType === 'team'
        ? (allTeams || []).filter((team) => String(team.id || team._id || '') === scopeId)
        : (allTeams || []).filter((team) => (
          (team.crews || []).some((crew) => String(crew.id || crew._id || '') === scopeId)
        ));

      if (scopeType === 'team') teamIds.add(scopeId);
      if (scopeType === 'crew') crewIds.add(scopeId);
      matchingTeams.forEach((team) => {
        if (scopeType === 'team' && (team?.id || team?._id)) teamIds.add(String(team.id || team._id));
        (team.crews || []).forEach((crew) => {
          const crewId = String(crew.id || crew._id || '');
          if (!crewId || (scopeType === 'crew' && crewId !== scopeId)) return;
          crewIds.add(crewId);
          (appUsers || [])
            .filter((user) => String(user.crew_id || '') === crewId)
            .forEach((user) => { if (user?.id) userIds.add(String(user.id)); });
          (crew.escalation_rules || []).forEach((rule) => {
            if (rule?.is_active === false) return;
            const refs = Array.isArray(rule?.escalated_to) ? rule.escalated_to : [rule?.escalated_to];
            refs.forEach((ref) => {
              const user = resolveUserRef(ref);
              if (user?.id) userIds.add(String(user.id));
            });
          });
        });
      });
    });

    userIds.delete('');
    userIds.delete('undefined');
    return {
      userIds: [...userIds],
      departmentIds: [...departmentIds],
      teamIds: [...teamIds],
      crewIds: [...crewIds],
    };
  }, [allDepartments, allTeams, appUsers, currentUser?.id, isAdmin, isIncidentScopedLeader, scopedLeaderGrants, serverRecipientScope]);

  // GIS tools state
  const persistent = usePersistentGisMap(id, !!id, !isReadOnly);

  // Live assigned-personnel layer — identical to the incident map widget.
  const personnel = useIncidentPersonnel(id);
  const personnelOnMap = personnel.filter((p) => p.lat != null && p.lng != null).length;
  const [addMode, setAddMode] = useState(false);
  const [drawShape, setDrawShape] = useState('none');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [pointMode, setPointMode] = useState(false);
  const [pointType, setPointType] = useState('assembly');
  const [pointDraft, setPointDraft] = useState(null);
  const [mapStyle, setMapStyle] = useState('standard');
  const [showGeoFence, setShowGeoFence] = useState(false);
  const [geoFenceActionFlow, setGeoFenceActionFlow] = useState(null);
  const geoFenceFocusCaptureActiveRef = useRef(false);
  const newestGeoFenceFocusRef = useRef(null);

  useEffect(() => {
    const captureCreatedCard = (event) => {
      if (!geoFenceFocusCaptureActiveRef.current) return;
      newestGeoFenceFocusRef.current = keepNewestCreatedTimelineFocus(
        newestGeoFenceFocusRef.current,
        event?.detail,
        id,
      );
    };
    window.addEventListener('timelineCardCreated', captureCreatedCard);
    return () => window.removeEventListener('timelineCardCreated', captureCreatedCard);
  }, [id]);

  useEffect(() => {
    if (map) {
      const options = buildMapStyleOptions(mapStyle, {
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
      });
      if (incident?.map_projection && !incident.map_projection.official) {
        options.styles = [
          ...(options.styles || []),
          { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 12 }] },
          { elementType: 'labels.icon', stylers: [{ saturation: -100 }] },
        ];
      }
      map.setOptions(options);
    }
  }, [incident?.map_projection, map, mapStyle]);

  const heatmapOn = !!persistent.settings.heatmapOn;
  const sequenceOn = !!persistent.settings.sequenceOn;
  const importance = persistent.settings.importance || 'response';
  const category = persistent.settings.category || 'Rescue';
  const setHeatmapOn = (u) => persistent.setSettings((s) => ({ ...s, heatmapOn: typeof u === 'function' ? u(!!s.heatmapOn) : u }));
  const setSequenceOn = (u) => persistent.setSettings((s) => ({ ...s, sequenceOn: typeof u === 'function' ? u(!!s.sequenceOn) : u }));
  const setImportance = (v) => persistent.setSettings((s) => ({ ...s, importance: v }));
  const setCategory = (v) => persistent.setSettings((s) => ({ ...s, category: v }));

  const activePlacementTool = useMemo(() => {
    if (pointMode) return 'Add Point';
    if (drawShape === 'polygon') return 'Draw Zone';
    if (drawShape === 'circle') return 'Radius';
    if (drawShape === 'polyline') return 'Draw Line';
    if (addMode) return 'Add Task';
    return '';
  }, [addMode, drawShape, pointMode]);

  useEffect(() => {
    if (!activePlacementTool) return undefined;
    const cancelActiveTool = (event) => {
      if (event.key !== 'Escape') return;
      setAddMode(false);
      setPointMode(false);
      setDrawShape('none');
    };
    window.addEventListener('keydown', cancelActiveTool);
    return () => window.removeEventListener('keydown', cancelActiveTool);
  }, [activePlacementTool]);

  const zoomMap = useCallback((step) => {
    if (!map) return;
    const currentZoom = Number(map.getZoom?.() || 14);
    map.setZoom(Math.max(2, Math.min(22, currentZoom + step)));
  }, [map]);

  const toggleMapFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
        return;
      }
      await mapFrameRef.current?.requestFullscreen?.();
    } catch (_error) {
      toast.error('Fullscreen is not available in this browser');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [
          incRes,
          familyRes,
          locRes,
          cardRes,
          operationsAreaRes,
          directoryRes,
        ] = await Promise.all([
          CommanServices.getIncidentDetail(id),
          CommanServices.getincidentsFamily(id),
          CommanServices.getlocationszonesdata(),
          isAdmin ? CommanServices.getactioncardsData() : Promise.resolve(null),
          CommanServices.getOperationsArea(),
          isExplicitLeader ? CommanServices.getIncidentRecipientDirectory(id).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setIncident(incRes?.data ?? incRes ?? null);
        const familyPayload = familyRes?.data ?? familyRes ?? {};
        setFamilyEntries(Array.isArray(familyPayload?.family_tree) ? familyPayload.family_tree : []);
        setZonesData(locRes?.status ? (locRes.data || []) : []);
        const list = isAdmin
          ? (Array.isArray(cardRes) ? cardRes : (cardRes?.data?.action_cards || cardRes?.data || []))
          : [];
        setActionCards(Array.isArray(list) ? list : []);
        const directory = directoryRes ? normalizeIncidentRecipientDirectory(directoryRes) : null;
        setAppUsers(directory?.users || []);
        setAllTeams(directory?.teams || []);
        setAllDepartments(directory?.departments || []);
        setServerRecipientScope(directory ? {
          userIds: directory.allowedUserIds,
          departmentIds: directory.allowedDepartmentIds,
          teamIds: directory.allowedTeamIds,
          crewIds: directory.allowedCrewIds,
        } : null);
        const operationsPoint = operationsAreaRes?.data?.center;
        if (Number.isFinite(Number(operationsPoint?.latitude)) && Number.isFinite(Number(operationsPoint?.longitude))) {
          setOperationsCenter({ lat: Number(operationsPoint.latitude), lng: Number(operationsPoint.longitude) });
        }
      } catch (e) { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, isAdmin, isExplicitLeader]);

  const isScenario = !!incident?.is_scenario;
  const isRealIncident = !!incident && !incident.is_exercise && !incident.is_scenario;
  const backLabel = isScenario ? 'Back to Scenario' : (isRealIncident ? 'Back to Incident' : 'Back to Drill');
  const pageTitle = (isScenario || String(incident?.status).toLowerCase() === 'requested') ? 'Create Map' : 'Full Map';
  const canUseGeoFence = (isAdmin || isIncidentScopedLeader)
    && isGeoFenceOperationalIncident(incident, {
      readOnly: incidentIsClosed,
      scenarioMode: isScenario,
    });

  const navigateFromMap = useCallback((state = null) => {
    if (backTo) {
      navigate(backTo, state ? { state } : undefined);
      return;
    }
    if (state) {
      navigate(`/incident-details/${id}${isScenario ? '?scenario=1' : ''}`, { state });
      return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate(`/incident-details/${id}${isScenario ? '?scenario=1' : ''}`);
  }, [backTo, id, isScenario, navigate]);

  const handleBack = useCallback(() => navigateFromMap(), [navigateFromMap]);

  const openGeoFence = useCallback(() => {
    if (canUseGeoFence) setShowGeoFence(true);
  }, [canUseGeoFence]);

  const incidentWithCoords = useMemo(() => {
    if (!incident) return null;
    const zoneIndex = buildZoneIndex(zones);
    const resolved = resolveIncidentCoords(zoneIndex, incident);
    if (!resolved) return null;
    const zoneName = resolved.name || resolved.matchedName || '';
    return { ...incident, zone_name: zoneName, lat: resolved.lat, lng: resolved.lng };
  }, [incident, zones]);

  const geoFenceIncident = useMemo(() => {
    if (!incident) return null;
    const existing = incident.gps_coordinates || {};
    const latitude = existing.latitude ?? existing.lat ?? incidentWithCoords?.lat;
    const longitude = existing.longitude ?? existing.lng ?? incidentWithCoords?.lng;
    if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return incident;
    return {
      ...incident,
      gps_coordinates: {
        ...existing,
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
    };
  }, [incident, incidentWithCoords]);

  const geoFenceInvolvedUserIds = useMemo(
    () => (personnel || []).map((person) => String(person?.user_id || '')).filter(Boolean),
    [personnel]
  );

  const announceTimelineChange = useCallback((changeType = 'timeline_card_changed') => {
    announceIncidentTimelineChange(incident?.id, changeType);
    window.dispatchEvent(new Event('dashboardRefresh'));
    localStorage.setItem('dashboardNeedsRefresh', 'true');
  }, [incident?.id]);

  const beginGeoFenceFocusCapture = useCallback(() => {
    newestGeoFenceFocusRef.current = null;
    geoFenceFocusCaptureActiveRef.current = true;
  }, []);

  const cancelGeoFenceFocusCapture = useCallback(() => {
    geoFenceFocusCaptureActiveRef.current = false;
    newestGeoFenceFocusRef.current = null;
  }, []);

  useEffect(() => {
    if (canUseGeoFence) return;
    cancelGeoFenceFocusCapture();
    setShowGeoFence(false);
    setGeoFenceActionFlow(null);
  }, [canUseGeoFence, cancelGeoFenceFocusCapture]);

  const completeGeoFenceAction = useCallback((changeType, fallbackCreatedDetail = null) => {
    if (fallbackCreatedDetail) {
      newestGeoFenceFocusRef.current = keepNewestCreatedTimelineFocus(
        newestGeoFenceFocusRef.current,
        fallbackCreatedDetail,
        incident?.id,
      );
    }
    const timelineFocusRequest = newestGeoFenceFocusRef.current;
    geoFenceFocusCaptureActiveRef.current = false;
    newestGeoFenceFocusRef.current = null;
    setShowGeoFence(false);
    setGeoFenceActionFlow(null);
    announceTimelineChange(changeType);
    if (timelineFocusRequest) navigateFromMap({ timelineFocusRequest });
  }, [announceTimelineChange, incident?.id, navigateFromMap]);

  const handleGeoFenceAction = useCallback(async (type, userIds, userNames, fence) => {
    if (!canUseGeoFence || !incident?.id) {
      cancelGeoFenceFocusCapture();
      toast.error('Geo-fence actions are not available for this incident');
      return false;
    }
    if (!isAdmin) {
      const allowedUserIds = new Set((scopedRecipientScope?.userIds || []).map(String));
      if (userIds.some((userId) => !allowedUserIds.has(String(userId)))) {
        cancelGeoFenceFocusCapture();
        toast.error('One or more selected users are outside your incident scope');
        return false;
      }
    }
    const geoFenceTargeting = buildGeoFenceTargeting(userIds, fence);
    const selections = userIds.map((userId, index) => ({
      type: 'user',
      id: userId,
      name: userNames[index],
    }));
    const fenceNote = `geo-fence (${Number(fence?.radiusKm || 0).toFixed(1)} km circle, ${userIds.length} user${userIds.length === 1 ? '' : 's'})`;
    beginGeoFenceFocusCapture();

    if (type === 'notification') {
      let response;
      try {
        response = await CommanServices.notificationMaunal(incident.id, {
          user_ids: geoFenceTargeting.userIds,
          department_ids: [],
          crew_ids: [],
          force: true,
          is_attention_only: false,
          source: 'geo_fence',
          geoFenceTargeting,
        });
      } catch (error) {
        cancelGeoFenceFocusCapture();
        toast.error(error?.message || 'Failed to create and deliver the geo-fence notification');
        return false;
      }
      const outcome = manualNotificationOutcome(response, geoFenceTargeting.userIds.length);
      if (!outcome.ok) {
        if (outcome.partial) {
          toast.warning(`${outcome.deliveredCount ?? 0} notification${outcome.deliveredCount === 1 ? '' : 's'} delivered; ${outcome.failedCount} failed`);
        } else {
          toast.error(outcome.message || 'Failed to create and deliver the geo-fence notification');
        }
        cancelGeoFenceFocusCapture();
        return false;
      }
      if (!outcome.timelineItemId) {
        cancelGeoFenceFocusCapture();
        toast.warning('Delivery was accepted, but the notification timeline card could not be confirmed. Retry to finish safely.');
        return false;
      }
      toast.success(`Notification created for ${outcome.deliveredCount ?? userIds.length} user${userIds.length === 1 ? '' : 's'} inside the circle`);
      try {
        await CommanServices.addIncidentAuditLog(incident.id, {
          action: 'Notified',
          detail: `Sent a manual notification via ${fenceNote}`,
        });
      } catch (_error) { /* audit is best-effort */ }
      completeGeoFenceAction('geo_fence_notification_created', {
        incidentId: incident.id,
        itemId: outcome.timelineItemId,
        group: 'notification',
      });
      return true;
    }

    if (!['verification', 'status', 'action_card'].includes(type)) {
      cancelGeoFenceFocusCapture();
      toast.error('Unsupported geo-fence action');
      return false;
    }

    setShowGeoFence(false);
    setGeoFenceActionFlow({ type, selections, userIds, fence, geoFenceTargeting });
    return true;
  }, [beginGeoFenceFocusCapture, canUseGeoFence, cancelGeoFenceFocusCapture, completeGeoFenceAction, incident, isAdmin, scopedRecipientScope]);

  const familyMarkers = useMemo(() => {
    const zoneIndex = buildZoneIndex(zones);
    const source = familyEntries.length > 0
      ? familyEntries
      : (incident ? [{ incident, depth: 0, parent_id: incident.parent_incident_id || null }] : []);
    const resolved = source.map((entry) => {
      const raw = String(entry?.incident?.id) === String(incident?.id)
        ? { ...entry.incident, ...incident }
        : entry?.incident;
      if (!raw?.id) return null;
      const point = resolveIncidentCoords(zoneIndex, raw);
      if (!point) return null;
      return {
        ...raw,
        id: String(raw.id),
        lat: Number(point.lat),
        lng: Number(point.lng),
        zone_name: point.name || point.matchedName || '',
        depth: Number(entry?.depth || 0),
        parent_id: entry?.parent_id || raw.parent_incident_id || null,
      };
    }).filter(Boolean);
    return spreadOverlappingFamilyMarkers(resolved, String(id));
  }, [familyEntries, id, incident, zones]);

  const familyById = useMemo(() => new Map(familyMarkers.map((entry) => [String(entry.id), entry])), [familyMarkers]);
  const familyRelations = useMemo(() => familyMarkers.flatMap((child) => {
    const parentId = String(child.parent_id || child.parent_incident_id || '').trim();
    const parent = familyById.get(parentId);
    if (!parent) return [];
    return [{
      id: `${parent.id}-${child.id}`,
      parent,
      child,
      midpoint: { lat: (Number(parent.lat) + Number(child.lat)) / 2, lng: (Number(parent.lng) + Number(child.lng)) / 2 },
      path: [{ lat: Number(parent.lat), lng: Number(parent.lng) }, { lat: Number(child.lat), lng: Number(child.lng) }],
    }];
  }), [familyById, familyMarkers]);

  const center = useMemo(() => (
    incidentWithCoords ? { lat: Number(incidentWithCoords.lat), lng: Number(incidentWithCoords.lng) } : operationsCenter
  ), [incidentWithCoords, operationsCenter]);

  useEffect(() => {
    if (!map || familyMarkers.length === 0 || !window.google?.maps) return;
    if (familyMarkers.length === 1) {
      map.panTo({ lat: Number(familyMarkers[0].lat), lng: Number(familyMarkers[0].lng) });
      map.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    familyMarkers.forEach((entry) => bounds.extend({ lat: Number(entry.lat), lng: Number(entry.lng) }));
    map.fitBounds(bounds, 80);
    const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > 17) map.setZoom(17);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [familyMarkers, map]);

  const handleMapClick = useCallback((e) => {
    if (isReadOnly) return;
    const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    if (pointMode) {
      setPointDraft({ position, type: pointType, label: '', info: '', details: [], incidentId: id });
      setPointMode(false);
      return;
    }
    if (!addMode) return;
    setDraft({ position, title: '', note: '', category, importance, status: 'open', incidentId: id });
    setAddMode(false);
  }, [addMode, pointMode, pointType, category, importance, id, isReadOnly]);

  const saveTask = useCallback((form) => {
    persistent.setTasks((prev) => form.id
      ? prev.map((t) => (t.id === form.id ? { ...t, ...form } : t))
      : [...prev, { ...form, id: `task_${Date.now()}`, createdAt: Date.now(), incidentId: id }]);
    setDraft(null);
  }, [persistent, id]);

  const savePoint = useCallback((form) => {
    persistent.setPoints((prev) => form.id
      ? prev.map((p) => (p.id === form.id ? { ...p, ...form } : p))
      : [...prev, { ...form, id: `pt_${Date.now()}`, createdAt: Date.now(), incidentId: id }]);
    setPointDraft(null);
  }, [persistent, id]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin mb-4" />
        <p className="text-slate-400 font-medium">Loading map…</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50" data-testid="exercise-map-page">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2.5 sm:gap-3 sm:px-5 sm:py-3">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button onClick={handleBack} data-testid="exercise-map-back-btn"
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 sm:px-3">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{backLabel}</span>
            <span className="sm:hidden">Back</span>
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">{pageTitle}</h1>
            <p className="text-xs text-gray-500 truncate">
              {incident?.incident_type || incident?.title || 'Exercise'}
              {incidentWithCoords?.zone_name ? ` · ${incidentWithCoords.zone_name}` : ''}
              {isScenario ? ' · Scenario Template' : ''}
              {personnel.length > 0 ? ` · Personnel on map: ${personnelOnMap}/${personnel.length}` : ''}
            </p>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <label className="flex items-center gap-2">
            <span className="hidden text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 sm:inline">Map style</span>
            <MapStyleSelector value={mapStyle} onChange={setMapStyle} testid="exercise-map-style" className="max-w-[92px] sm:max-w-none" />
          </label>
          {isReadOnly ? (
            <span
              className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1.5 text-[10px] font-bold text-slate-600 sm:px-3 sm:text-xs"
              title={incidentIsClosed ? 'Resolved · read-only' : 'Read-only map'}
            >
              <span className="sm:hidden">Read-only</span>
              <span className="hidden sm:inline">{incidentIsClosed ? 'Resolved · read-only' : 'Read-only map'}</span>
            </span>
          ) : (
            <GisSaveControl saveStatus={persistent.saveStatus} onSave={persistent.saveNow} statusFirst compactOnSmall />
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {/* Map */}
        <main ref={mapFrameRef} className="relative h-full w-full overflow-hidden bg-slate-100">
          {/* Floating tools card — overlays the map instead of consuming map width. */}
          {isReadOnly ? (
            <div className="absolute left-[14px] top-[14px] z-30 max-h-[calc(100%_-_150px)] w-[264px] max-w-[calc(100%_-_28px)] overflow-y-auto rounded-[14px] sm:max-h-[calc(100%_-_28px)]">
              <div className="space-y-3 rounded-[14px] border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur">
                <div className="text-xs leading-relaxed text-slate-600">
                  {incidentIsClosed
                    ? 'This incident is resolved. The official map and its saved GIS layers are read-only.'
                    : 'You can review this incident map. Only administrators can edit its GIS layers.'}
                </div>
                {canUseGeoFence && (
                  <button
                    type="button"
                    onClick={openGeoFence}
                    data-testid="exercise-map-geofence-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
                  >
                    <Target className="h-4 w-4" /> Geo-Fence
                  </button>
                )}
              </div>
            </div>
          ) : <MapGisPanel
              variant="floating"
              tasks={persistent.tasks}
              addMode={addMode} setAddMode={setAddMode}
              drawShape={drawShape} setDrawShape={setDrawShape}
              heatmapOn={heatmapOn} setHeatmapOn={setHeatmapOn}
              sequenceOn={sequenceOn} setSequenceOn={setSequenceOn}
              activeImportance={importance} setActiveImportance={setImportance}
              newCategory={category} setNewCategory={setCategory}
              pointMode={pointMode} setPointMode={setPointMode}
              pointType={pointType} setPointType={setPointType}
              points={persistent.points}
              onGeoFence={canUseGeoFence ? openGeoFence : null}
              onClear={() => { persistent.setTasks([]); persistent.setZones([]); persistent.setPoints([]); }}
            />}
          {mapsUnavailable ? (
            <MapUnavailable center={center} label="Exercise map" />
          ) : isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={14}
              onLoad={setMap}
              onClick={handleMapClick}
              options={buildMapStyleOptions(mapStyle, {
                mapTypeControl: false,
                zoomControl: false,
                fullscreenControl: false,
                draggableCursor: (addMode || pointMode || drawShape !== 'none') ? 'crosshair' : undefined,
              })}
            >
              {familyRelations.map((relation) => {
                const selected = selectedRelationId === relation.id;
                return (
                  <React.Fragment key={relation.id}>
                    <Polyline
                      path={relation.path}
                      onClick={() => setSelectedRelationId(relation.id)}
                      options={{
                        strokeColor: selected ? '#f97316' : '#a855f7',
                        strokeOpacity: 0,
                        strokeWeight: 14,
                        clickable: true,
                        geodesic: true,
                        zIndex: selected ? 90 : 55,
                        icons: [{
                          icon: {
                            path: 'M 0,-1 0,1',
                            strokeColor: selected ? '#f97316' : '#a855f7',
                            strokeOpacity: 1,
                            strokeWeight: selected ? 3 : 2,
                            scale: 3,
                          },
                          offset: '0',
                          repeat: '18px',
                        }],
                      }}
                    />
                    {selected && (
                      <InfoWindow position={relation.midpoint} onCloseClick={() => setSelectedRelationId(null)}>
                        <div className="min-w-[230px] p-1" data-testid={`incident-relation-${relation.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600">Linked incidents</p>
                          <p className="mt-1 text-xs font-semibold text-gray-800">Root/parent: {relation.parent.id}</p>
                          <p className="text-xs font-semibold text-gray-800">Related: {relation.child.id}</p>
                          <p className="mt-1 text-[10px] text-gray-500">Select either incident marker to review its card.</p>
                        </div>
                      </InfoWindow>
                    )}
                  </React.Fragment>
                );
              })}

              {familyMarkers.map((familyIncident) => {
                const isCurrent = String(familyIncident.id) === String(id);
                const isOfficial = familyIncident?.map_projection ? !!familyIncident.map_projection.official : true;
                const infoSelected = selectedIncidentInfoId === String(familyIncident.id);
                return (
                  <Marker
                    key={`family-${familyIncident.id}`}
                    position={{ lat: Number(familyIncident.lat), lng: Number(familyIncident.lng) }}
                    onClick={() => setSelectedIncidentInfoId(String(familyIncident.id))}
                    zIndex={isCurrent ? 180 : 150}
                    label={!isCurrent ? { text: 'R', color: '#ffffff', fontSize: '10px', fontWeight: '700' } : undefined}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: isCurrent ? 10 : 9,
                      fillColor: isCurrent ? (isOfficial ? '#ef4444' : '#64748b') : '#0891b2',
                      fillOpacity: 1,
                      strokeColor: isCurrent ? '#fbbf24' : '#ffffff',
                      strokeWeight: isCurrent ? 3 : 2,
                    }}
                  >
                    {infoSelected && (
                      <InfoWindow onCloseClick={() => setSelectedIncidentInfoId(null)}>
                        <div className="min-w-[230px] max-w-[280px] p-1" data-testid={`exercise-map-info-${familyIncident.id}`}>
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${isCurrent ? 'text-amber-600' : 'text-cyan-700'}`}>
                            {isCurrent ? 'Current incident' : 'Related incident'}
                          </p>
                          <p className="mt-1 text-sm font-bold text-gray-900">{familyIncident.incident_type || familyIncident.title || 'Incident'}</p>
                          <p className="mt-0.5 text-[11px] font-semibold text-gray-500">{familyIncident.id}</p>
                          {familyIncident.zone_name && <p className="mt-1 text-xs text-gray-600">{familyIncident.zone_name}</p>}
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              onClick={() => navigate(`/incident-details/${familyIncident.id}`)}
                              className="text-[11px] font-bold text-indigo-600 hover:underline"
                            >
                              Open incident
                            </button>
                            {isCurrent && (
                              <button onClick={handleBack} className="text-[11px] font-bold text-gray-500 hover:underline">
                                {backLabel}
                              </button>
                            )}
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                );
              })}

              {/* Assigned personnel — live green dots, same layer as the widget */}
              <PersonnelMarkers personnel={personnel} />

              <MapGisOverlay
                tasks={persistent.tasks} setTasks={persistent.setTasks}
                zones={persistent.zones} setZones={persistent.setZones}
                points={persistent.points} setPoints={persistent.setPoints}
                onEditPoint={(p) => setPointDraft(p)}
                drawShape={drawShape} setDrawShape={setDrawShape}
                heatmapOn={heatmapOn} sequenceOn={sequenceOn}
                activeImportance={importance}
                selectedTaskId={selectedTaskId} setSelectedTaskId={setSelectedTaskId}
                onEditTask={(t) => { setDraft(t); setSelectedTaskId(null); }}
                incidents={familyMarkers}
                incidentId={id}
                readOnly={isReadOnly}
                importanceColors={FULL_MAP_IMPORTANCE_COLORS}
              />
            </GoogleMap>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
          )}

          {activePlacementTool && (
            <div
              className="pointer-events-none absolute bottom-[86px] left-[14px] right-[66px] z-30 flex justify-center sm:bottom-auto sm:left-[292px] sm:top-[14px]"
              data-testid="gis-active-tool-hint"
            >
              <span className="max-w-full truncate whitespace-nowrap rounded-full bg-slate-900/90 px-4 py-2 text-[11px] font-bold text-white shadow-lg backdrop-blur">
                {activePlacementTool} — click the map to place · Esc to cancel
              </span>
            </div>
          )}

          <div className="absolute bottom-[14px] right-[14px] z-30 flex flex-col gap-2" data-testid="gis-map-controls">
            <button
              type="button"
              onClick={() => zoomMap(1)}
              disabled={!map}
              aria-label="Zoom in"
              className="flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-lg transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => zoomMap(-1)}
              disabled={!map}
              aria-label="Zoom out"
              className="flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-lg transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Minus className="h-[18px] w-[18px]" />
            </button>
            <button
              type="button"
              onClick={toggleMapFullscreen}
              aria-label="Toggle map fullscreen"
              className="flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-lg transition-colors hover:bg-slate-50"
            >
              <Maximize2 className="h-[17px] w-[17px]" />
            </button>
          </div>

          <MapGisKpi variant="strip" tasks={persistent.tasks} incidents={familyMarkers} />
          {!isReadOnly && <TaskFormModal draft={draft} onSave={saveTask} onClose={() => setDraft(null)} actionCards={actionCards} />}
          {!isReadOnly && <PointFormModal draft={pointDraft} onSave={savePoint} onClose={() => setPointDraft(null)} />}
        </main>
      </div>

      {showGeoFence && canUseGeoFence && geoFenceIncident && (
        <GeoFenceModal
          incident={geoFenceIncident}
          involvedUserIds={geoFenceInvolvedUserIds}
          allowedUserIds={scopedRecipientScope?.userIds ?? null}
          onClose={() => {
            cancelGeoFenceFocusCapture();
            setShowGeoFence(false);
          }}
          onAction={handleGeoFenceAction}
        />
      )}

      {canUseGeoFence && geoFenceActionFlow?.type === 'verification' && (
        <AcknowledgmentModal
          incident={incident}
          mode="config"
          prefilledSelections={geoFenceActionFlow.selections}
          geoFenceTargeting={geoFenceActionFlow.geoFenceTargeting}
          allowedRecipientUserIds={scopedRecipientScope?.userIds ?? null}
          allowedRecipientDepartmentIds={scopedRecipientScope?.departmentIds ?? null}
          allowedRecipientTeamIds={scopedRecipientScope?.teamIds ?? null}
          allowedRecipientCrewIds={scopedRecipientScope?.crewIds ?? null}
          onClose={() => {
            cancelGeoFenceFocusCapture();
            setGeoFenceActionFlow(null);
          }}
          onSuccess={() => completeGeoFenceAction('geo_fence_verification_created')}
        />
      )}

      {canUseGeoFence && geoFenceActionFlow?.type === 'action_card' && (
        <ResponseActionModal
          incident={incident}
          prefilledName=""
          prefilledUserIds={[]}
          prefilledSelections={geoFenceActionFlow.selections}
          geoFenceTargeting={geoFenceActionFlow.geoFenceTargeting}
          isImmediateTask={false}
          allowedRecipientUserIds={scopedRecipientScope?.userIds ?? null}
          allowedRecipientDepartmentIds={scopedRecipientScope?.departmentIds ?? null}
          allowedRecipientTeamIds={scopedRecipientScope?.teamIds ?? null}
          allowedRecipientCrewIds={scopedRecipientScope?.crewIds ?? null}
          onClose={() => {
            cancelGeoFenceFocusCapture();
            setGeoFenceActionFlow(null);
          }}
          onSuccess={() => completeGeoFenceAction('geo_fence_action_card_created')}
        />
      )}

      {canUseGeoFence && geoFenceActionFlow?.type === 'status' && (
        <StatusUpdateModal
          incident={incident}
          groupKey="status_update"
          isAdmin={isAdmin}
          geoFenceTargeting={geoFenceActionFlow.geoFenceTargeting}
          onClose={() => {
            cancelGeoFenceFocusCapture();
            setGeoFenceActionFlow(null);
          }}
          onSuccess={() => {
            const userIds = geoFenceActionFlow.userIds || [];
            if (userIds.length) {
              toast.success(`Status update created for ${userIds.length} geo-fenced user${userIds.length === 1 ? '' : 's'}`);
            }
            completeGeoFenceAction('geo_fence_status_update_created');
          }}
        />
      )}
    </div>
  );
};

export default ExerciseMapPage;
